/**
 * Rezept aus einer Webseite übernehmen (nur für den privaten Gebrauch).
 *
 * Der Server liest die strukturierten Daten der Seite (schema.org/Recipe) und
 * liefert einen Entwurf. Gespeichert wird erst, wenn die Person ihn geprüft
 * hat. Geteilte Links (Teilen-Menü des Handys) landen über ?url= hier.
 */
import { useEffect, useRef, useState } from 'react';
import { ApiError, api } from '../api/client.ts';
import type { RecipeDraft } from '../api/types.ts';
import { texts } from '../texts/de.ts';

/** Antwort des Servers. */
export interface ImportResult {
  draft: RecipeDraft;
  imageUrl: string | null;
  sourceUrl: string;
}

/** Erste Webadresse in einem Text, zum Beispiel aus dem Teilen-Menü. */
export function urlFromShared(text: string | null): string {
  return /https?:\/\/\S+/i.exec(text ?? '')?.[0] ?? '';
}

/** Eigenschaften des Imports. */
export interface WebImportProps {
  /** Vorbelegte Adresse, zum Beispiel aus dem Teilen-Menü. */
  readonly initialUrl: string;
  readonly onImported: (result: ImportResult) => void;
}

/** Eingabe für den Import aus einer Webseite. */
export function WebImport({ initialUrl, onImported }: WebImportProps) {
  const [url, setUrl] = useState(initialUrl);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const started = useRef(false);

  /** Liest die Seite und übergibt den Entwurf. */
  async function run(address: string) {
    setBusy(true);
    setMessage(null);
    try {
      const result = await api.post<ImportResult>('/api/import', { url: address });
      onImported(result);
      setMessage(texts.webImport.done.replace('{count}', String(result.draft.ingredients.length)));
    } catch (caught) {
      setMessage(caught instanceof ApiError ? caught.message : texts.common.error);
    } finally {
      setBusy(false);
    }
  }

  // Geteilte Links gleich einlesen.
  useEffect(() => {
    if (initialUrl !== '' && !started.current) {
      started.current = true;
      void run(initialUrl);
    }
    // Nur beim ersten Anzeigen mit vorbelegter Adresse.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialUrl]);

  return (
    <details
      open={initialUrl !== ''}
      className="mb-4 rounded-card border border-border-subtle bg-surface-raised p-4"
    >
      <summary className="cursor-pointer font-semibold">{texts.webImport.title}</summary>
      <p className="mt-2 text-xs text-content-muted">{texts.webImport.hint}</p>
      <form
        className="mt-3 flex gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          void run(url);
        }}
      >
        <input
          type="url"
          value={url}
          onChange={(event) => setUrl(event.target.value)}
          placeholder="https://"
          aria-label={texts.webImport.address}
          className="flex-1 rounded-xl border border-border-subtle bg-surface px-3 py-2 text-sm"
        />
        <button
          type="submit"
          disabled={busy || url.trim() === ''}
          className="rounded-xl bg-accent px-4 text-sm font-semibold text-on-accent disabled:opacity-50"
        >
          {busy ? texts.common.loading : texts.webImport.run}
        </button>
      </form>
      {message === null ? null : (
        <p className="mt-2 text-sm text-content-muted" role="status">
          {message}
        </p>
      )}
    </details>
  );
}
